// Author: Ahsan Saleem
/**
 * BloomShell — the power layer, and a real command RUNTIME (not just navigation).
 * Cmd/Ctrl+K runs commands; a command either jumps somewhere, does something
 * immediately, or captures a few arguments inline and EXECUTES an operation —
 * recording a reading, capturing a note — through the same offline outbox the
 * screens use, then confirms with a toast. You act without leaving your context.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Activity, ListChecks, TriangleAlert, Sprout, NotebookPen, Settings2, SunMoon, LogOut,
  Search, CornerDownLeft, MapPin, FlaskConical, BarChart3, LayoutGrid, Clock3, Wrench, Cpu,
  Leaf, StickyNote, RefreshCw, ChevronLeft, Loader2, Play, Sparkles, ShieldAlert,
} from 'lucide-react';
import { auth, acknowledgeAlert, transitionBatch, getBatches, metricSuffix } from '../api.js';
import { toggleTheme } from './theme.js';
import { visibleWorkspaces, hasCap } from '../session.js';
import { useSystem } from './system.js';
import { enqueue, newIdempotencyKey } from '../outbox.js';
import { toast } from './toast.js';

// Batch lifecycle steps (mirrors the server state machine). Only FORWARD steps are
// offered as a one-shot command — holds/cancels stay on the full Crops screen where
// they belong; the API enforces validity and segregation-of-duties regardless.
const NEXT_BATCH: Record<string, Array<{ event: string; label: string }>> = {
  PLANNED: [{ event: 'SOW', label: 'Sow' }],
  SOWN: [{ event: 'ACTIVATE', label: 'Activate' }],
  ACTIVE: [{ event: 'START_HARVEST', label: 'Start harvest' }],
  QUARANTINED: [{ event: 'RELEASE', label: 'Release' }],
  HARVESTING: [{ event: 'COMPLETE', label: 'Complete' }],
};

interface CmdField {
  key: string; label: string; kind: 'select' | 'number' | 'text';
  options?: Array<{ value: string; label: string }>; placeholder?: string; hint?: string; default?: string; required?: boolean;
}
interface CmdForm { fields?: CmdField[]; load?: () => Promise<CmdField[]>; offline?: boolean; submitLabel: string; run: (v: Record<string, string>) => Promise<string>; }
interface Cmd {
  id: string; label: string; hint?: string; kbd?: string; group: string; icon: typeof Activity;
  keywords?: string; cap?: string; run?: () => void; form?: CmdForm;
}
const HIST_KEY = 'bloomos_cmd_history';

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [history, setHistory] = useState<string[]>(() => { try { return JSON.parse(localStorage.getItem(HIST_KEY) || '[]'); } catch { return []; } });
  // Argument-capture ("run") mode: the command whose operation we're about to execute.
  const [running, setRunning] = useState<Cmd | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();
  const [formFields, setFormFields] = useState<CmdField[]>([]);
  const [formLoading, setFormLoading] = useState(false);
  const navigate = useNavigate();

  const { user, snapshot, reload } = useSystem();
  const caps = user?.capabilities;
  const siteId = auth.siteId!;
  const points = snapshot?.latest ?? [];

  const commands = useMemo<Cmd[]>(() => {
    const go = (path: string) => () => { navigate(path); setOpen(false); };
    const ws = visibleWorkspaces(caps).map((w): Cmd => ({ id: 'ws-' + w.id, label: `Go to ${w.label}`, hint: 'Workspace', kbd: `⌘${w.short}`, group: 'Workspaces', icon: LayoutGrid, run: go(w.apps[0]!.to), keywords: w.apps.map((a) => a.label).join(' ') }));
    const zones = [1, 2, 3, 4].map((n): Cmd => ({ id: 'zone-' + n, label: `Open Zone ${n}`, hint: 'Facility', group: 'Objects', icon: MapPin, run: go('/v2/canvas'), keywords: 'zone facility' }));

    // Real, executable operations — the difference between a menu and a command line.
    const dpOptions = points.map((p) => ({ value: p.datapointCode, label: `${p.datapointCode} · last ${p.value ?? '—'}${p.unit ? ' ' + p.unit : ''}` }));
    const openAlerts = (snapshot?.alerts ?? []).filter((a) => (a.state ?? '').toUpperCase() === 'OPEN');
    const actions: Cmd[] = [
      { id: 'act-reading', label: 'Record a reading', hint: 'Action', group: 'Do', icon: Leaf, keywords: 'observation measure sensor capture value ops', cap: 'ops.write',
        form: {
          offline: true,
          submitLabel: 'Record reading',
          fields: [
            { key: 'datapoint', label: 'Measurement', kind: 'select', options: dpOptions, default: points[0]?.datapointCode, required: true },
            { key: 'value', label: 'Value', kind: 'number', placeholder: '0.0', default: points[0]?.value != null ? String(points[0].value) : '', hint: 'Confirm the reading — don’t retype from memory.', required: true },
            { key: 'note', label: 'Note (optional)', kind: 'text', placeholder: 'Context' },
          ],
          run: async (v) => {
            const code = v.datapoint || points[0]?.datapointCode;
            const val = Number(v.value);
            if (!code) throw new Error('No measurement channel available');
            if (!Number.isFinite(val)) throw new Error('Enter a numeric value');
            const unit = points.find((p) => p.datapointCode === code)?.unit;
            const idempotencyKey = newIdempotencyKey();
            const body: Record<string, unknown> = { idempotencyKey, kind: 'READING', observedAt: new Date().toISOString(), datapointCode: code, value: val };
            if (unit) body.unit = unit;
            if (v.note?.trim()) body.text = v.note.trim();
            await enqueue({ label: `READING: ${code} = ${val}`, method: 'POST', path: `/observations?siteId=${siteId}`, body, idempotencyKey });
            return `Reading recorded — ${code} = ${val}${unit ? ' ' + unit : ''}`;
          },
        } },
      { id: 'act-note', label: 'Capture a note', hint: 'Action', group: 'Do', icon: StickyNote, keywords: 'observation scouting comment log ops', cap: 'ops.write',
        form: {
          offline: true,
          submitLabel: 'Capture note',
          fields: [{ key: 'text', label: 'Observation', kind: 'text', placeholder: 'What did you observe?', required: true }],
          run: async (v) => {
            const idempotencyKey = newIdempotencyKey();
            await enqueue({ label: `NOTE: ${v.text.slice(0, 24)}`, method: 'POST', path: `/observations?siteId=${siteId}`, body: { idempotencyKey, kind: 'NOTE', observedAt: new Date().toISOString(), text: v.text.trim() }, idempotencyKey });
            return 'Note captured';
          },
        } },
      { id: 'act-refresh', label: 'Refresh facility data', hint: 'Action', group: 'Do', icon: RefreshCw, keywords: 'reload sync update snapshot', run: () => { reload(); setOpen(false); toast('Facility data refreshed', 'info'); } },
      ...(openAlerts.length ? [{
        id: 'act-ack', label: 'Acknowledge an alert', hint: 'Action', group: 'Do', icon: ShieldAlert, keywords: 'alert exception acknowledge ack recover', cap: 'ops.write',
        form: {
          submitLabel: 'Acknowledge',
          fields: [
            { key: 'alert', label: 'Open alert', kind: 'select', required: true, default: openAlerts[0]?.id,
              options: openAlerts.map((a) => ({ value: a.id, label: `${a.rule_name} · ${metricSuffix(a.metric)}${a.opened_value != null ? ` (${a.opened_value})` : ''}` })) },
            { key: 'note', label: 'Note (optional)', kind: 'text', placeholder: 'What you did / observed' },
          ],
          run: async (v) => { await acknowledgeAlert(v.alert, v.note?.trim() || undefined); return 'Alert acknowledged'; },
        },
      } as Cmd] : []),
      { id: 'act-advance', label: 'Advance a batch', hint: 'Action', group: 'Do', icon: Sprout, keywords: 'crop batch stage lifecycle sow activate harvest complete', cap: 'ops.write',
        form: {
          submitLabel: 'Advance batch',
          load: async () => {
            const batches = await getBatches(siteId);
            const opts = batches.flatMap((b) => (NEXT_BATCH[b.state] ?? []).map((t) => ({ value: `${b.id}|${t.event}`, label: `${b.code} — ${t.label}` })));
            return [
              { key: 'target', label: 'Batch → next step', kind: 'select', required: true, default: opts[0]?.value, options: opts },
              { key: 'reason', label: 'Reason (optional)', kind: 'text', placeholder: 'Context for the transition' },
            ];
          },
          run: async (v) => {
            const [id, event] = (v.target || '').split('|');
            if (!id || !event) throw new Error('Pick a batch to advance');
            await transitionBatch(id, event, v.reason?.trim() || undefined);
            return `Batch advanced — ${event.replace(/_/g, ' ').toLowerCase()}`;
          },
        } },
      { id: 'act-ai', label: 'Facility intelligence', hint: 'AI', group: 'Do', icon: Sparkles, keywords: 'insights ai signals anomaly predict explain suggest', run: () => { window.dispatchEvent(new Event('bloomos-open-insights')); setOpen(false); } },
    ];

    const all: Cmd[] = [
      ...ws,
      ...actions,
      { id: 'pulse', label: 'Facility Pulse', group: 'Applications', icon: Activity, run: go('/v2'), keywords: 'home overview', cap: 'read' },
      { id: 'today', label: 'Today', group: 'Applications', icon: ListChecks, run: go('/v2/work'), keywords: 'work tasks', cap: 'read' },
      { id: 'facility', label: 'Facility', group: 'Applications', icon: MapPin, run: go('/v2/canvas'), keywords: 'twin zones', cap: 'read' },
      { id: 'alerts', label: 'Review active alerts', group: 'Applications', icon: TriangleAlert, run: go('/v2/alerts'), keywords: 'exception incident', cap: 'read' },
      { id: 'maint', label: 'Maintenance & calibration', group: 'Applications', icon: Wrench, run: go('/v2/maintenance'), keywords: 'asset service calibrate upkeep', cap: 'read' },
      { id: 'prod', label: 'Production', group: 'Applications', icon: Sprout, run: go('/v2/production'), keywords: 'harvest cost', cap: 'read' },
      { id: 'reports', label: 'Reports', group: 'Applications', icon: BarChart3, run: go('/v2/reports'), keywords: 'trend analyse', cap: 'read' },
      { id: 'record', label: 'Open the Record screen', group: 'Applications', icon: NotebookPen, run: go('/v2/record'), keywords: 'reading photo observation history', cap: 'ops.write' },
      ...(import.meta.env.DEV ? [{ id: 'sim', label: 'Open Simulation Console', group: 'Applications', icon: FlaskConical, run: go('/simulation'), keywords: 'fault inject scenario', cap: 'admin' } as Cmd] : []),
      { id: 'edge', label: 'Edge — devices & commands', group: 'Applications', icon: Cpu, run: go('/v2/edge'), keywords: 'gateway device command shadow plc actuator', cap: 'read' },
      ...zones,
      { id: 'theme', label: 'Switch Command / Field mode', hint: 'Theme', group: 'System', icon: SunMoon, run: () => { toggleTheme(); setOpen(false); }, keywords: 'dark light' },
      { id: 'settings', label: 'Settings', group: 'System', icon: Settings2, run: go('/v2/settings') },
      { id: 'signout', label: 'Sign out', group: 'System', icon: LogOut, run: () => { auth.token = null; auth.siteId = null; auth.siteName = null; sessionStorage.removeItem('bloomos_booted'); navigate('/login'); setOpen(false); }, keywords: 'logout' },
    ];
    return all.filter((c) => !c.cap || hasCap(caps, c.cap));
  }, [navigate, caps, points, reload, siteId, snapshot]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) {
      const recent = history.map((id) => commands.find((c) => c.id === id)).filter(Boolean) as Cmd[];
      const rest = commands.filter((c) => !history.includes(c.id));
      return [...recent.map((c) => ({ ...c, group: 'Recent' })), ...rest];
    }
    return commands.filter((c) => (c.label + ' ' + (c.hint ?? '') + ' ' + (c.keywords ?? '')).toLowerCase().includes(s));
  }, [q, commands, history]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen((o) => !o); } };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('bloomos-open-cmdk', onOpen);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('bloomos-open-cmdk', onOpen); };
  }, []);
  useEffect(() => { if (open) { setQ(''); setActive(0); setRunning(null); setErr(undefined); setBusy(false); setFormFields([]); setFormLoading(false); } }, [open]);
  useEffect(() => { setActive(0); }, [q]);

  function pushHistory(id: string) {
    const next = [id, ...history.filter((h) => h !== id)].slice(0, 4);
    setHistory(next); localStorage.setItem(HIST_KEY, JSON.stringify(next));
  }
  function activate(c: Cmd) {
    pushHistory(c.id);
    if (!c.form) { c.run?.(); return; }
    const setup = (fields: CmdField[]) => {
      const init: Record<string, string> = {};
      for (const f of fields) init[f.key] = f.default ?? '';
      setFormFields(fields); setValues(init);
    };
    setErr(undefined); setBusy(false); setValues({}); setFormFields([]); setRunning(c);
    if (c.form.load) {
      setFormLoading(true);
      c.form.load().then(
        (fields) => { setup(fields); setFormLoading(false); },
        (e) => { setErr(e instanceof Error ? e.message : 'Could not load options'); setFormLoading(false); },
      );
    } else {
      setup(c.form.fields ?? []);
    }
  }
  async function submitForm(e: React.FormEvent) {
    e.preventDefault();
    if (!running?.form || formLoading) return;
    for (const f of formFields) if (f.required && !(values[f.key] ?? '').trim()) { setErr(`${f.label} is required`); return; }
    setBusy(true); setErr(undefined);
    try {
      const msg = await running.form.run(values);
      toast(msg, 'success');
      setOpen(false);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Could not complete'); setBusy(false);
    }
  }
  function onListKey(e: React.KeyboardEvent) {
    if (running) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); const c = filtered[active]; if (c) activate(c); }
  }

  let lastGroup = '';
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="cmdk-overlay" />
        <Dialog.Content className="cmdk" aria-label="BloomShell" onKeyDown={onListKey}
          onEscapeKeyDown={(e) => { if (running) { e.preventDefault(); setRunning(null); } }}>
          <Dialog.Title className="sr-only">BloomShell command palette</Dialog.Title>

          {running ? (
            <form className="cmdk-run" onSubmit={submitForm}>
              <div className="cmdk-run-head">
                <button type="button" className="cmdk-back" onClick={() => setRunning(null)} aria-label="Back to commands"><ChevronLeft size={16} aria-hidden /></button>
                <running.icon size={16} aria-hidden />
                <span className="cmdk-run-title">{running.label}</span>
                <kbd className="cmdk-esc">esc</kbd>
              </div>
              <div className="cmdk-fields">
                {formLoading ? (
                  <div className="cmdk-run-loading"><Loader2 size={16} className="v2-spin" aria-hidden /> Loading…</div>
                ) : formFields.map((f, i) => (
                  <label key={f.key} className="cmdk-field">
                    <span className="cmdk-field-l">{f.label}{f.required && <span className="cmdk-req" aria-hidden> *</span>}</span>
                    {f.kind === 'select' ? (
                      <select autoFocus={i === 0} value={values[f.key] ?? ''} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}>
                        {(f.options ?? []).length === 0 && <option value="">Nothing available</option>}
                        {(f.options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : (
                      <input autoFocus={i === 0} type={f.kind === 'number' ? 'number' : 'text'} inputMode={f.kind === 'number' ? 'decimal' : undefined}
                        step={f.kind === 'number' ? 'any' : undefined} placeholder={f.placeholder}
                        value={values[f.key] ?? ''} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} />
                    )}
                    {f.hint && <span className="cmdk-field-hint">{f.hint}</span>}
                  </label>
                ))}
                {err && <p className="cmdk-err" role="alert">{err}</p>}
              </div>
              <div className="cmdk-run-foot">
                <span className="cmdk-run-note">{running.form!.offline ? 'Saved through the offline queue · syncs when online' : 'Runs now against the live facility'}</span>
                <button className="cmdk-run-go" type="submit" disabled={busy || formLoading}>
                  {busy ? <Loader2 size={15} className="v2-spin" aria-hidden /> : <Play size={14} aria-hidden />}
                  {running.form!.submitLabel}
                  <kbd>↵</kbd>
                </button>
              </div>
            </form>
          ) : (
            <>
              <div className="cmdk-search">
                <Search size={17} aria-hidden />
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search or run a command…" aria-label="Search or run a command" />
                <kbd className="cmdk-esc">esc</kbd>
              </div>
              <div className="cmdk-list" role="listbox" aria-label="Commands">
                {filtered.length === 0 && <div className="cmdk-empty">No matching command</div>}
                {filtered.map((c, i) => {
                  const Icon = c.icon;
                  const showGroup = c.group !== lastGroup; lastGroup = c.group;
                  return (
                    <div key={c.id + i}>
                      {showGroup && <div className="cmdk-group label">{c.group === 'Recent' && <Clock3 size={11} style={{ marginRight: 4 }} />}{c.group}</div>}
                      <button role="option" aria-selected={i === active} className={`cmdk-item${i === active ? ' active' : ''}`}
                        onMouseMove={() => setActive(i)} onClick={() => activate(c)}>
                        <Icon size={16} aria-hidden />
                        <span className="cmdk-label">{c.label}</span>
                        {c.form && <span className="cmdk-tag">run</span>}
                        {c.kbd && <kbd className="cmdk-kbd">{c.kbd}</kbd>}
                        {c.hint && !c.kbd && !c.form && <span className="cmdk-hint">{c.hint}</span>}
                        {i === active && <CornerDownLeft size={13} className="cmdk-enter" aria-hidden />}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
